import React, { createContext, useContext, useState, useEffect } from 'react';

const ChatContext = createContext();

export const useChatContext = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChatContext must be used within a ChatProvider');
  }
  return context;
};

export const ChatProvider = ({ children }) => {
  const [activeSessions, setActiveSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [messages, setMessages] = useState({});
  const [isTyping, setIsTyping] = useState(false);

  useEffect(() => {
    // Load mock chat data
    const mockSessions = [
      {
        id: 1,
        customerName: 'John Doe',
        customerEmail: 'john@example.com',
        customerStatus: 'online',
        priority: 'high',
        subject: 'Login Issue',
        startedAt: new Date().toISOString(),
        lastMessage: 'I cannot access my account'
      },
      {
        id: 2,
        customerName: 'Jane Smith',
        customerEmail: 'jane@example.com',
        customerStatus: 'away',
        priority: 'medium',
        subject: 'Password Reset',
        startedAt: new Date(Date.now() - 300000).toISOString(),
        lastMessage: 'Need help with password reset'
      }
    ];

    const mockMessages = {
      1: [
        {
          id: 1,
          text: 'Hello, I need help with my account',
          sender: 'customer',
          timestamp: new Date(Date.now() - 600000).toISOString()
        },
        {
          id: 2,
          text: 'Hello John! I\'m here to help you. What seems to be the issue?',
          sender: 'agent',
          timestamp: new Date(Date.now() - 590000).toISOString()
        },
        {
          id: 3,
          text: 'I cannot access my account',
          sender: 'customer',
          timestamp: new Date(Date.now() - 580000).toISOString()
        }
      ],
      2: [
        {
          id: 1,
          text: 'Hi, I need help with password reset',
          sender: 'customer',
          timestamp: new Date(Date.now() - 300000).toISOString()
        },
        {
          id: 2,
          text: 'Hello Jane! I can help you with that. Let me guide you through the process.',
          sender: 'agent',
          timestamp: new Date(Date.now() - 290000).toISOString()
        }
      ]
    };

    setActiveSessions(mockSessions);
    setMessages(mockMessages);
  }, []);

  const sendMessage = (sessionId, text, sender = 'customer') => {
    const newMessage = {
      id: Date.now(),
      text,
      sender,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => ({
      ...prev,
      [sessionId]: [...(prev[sessionId] || []), newMessage]
    }));

    // Update session last message
    setActiveSessions(prev =>
      prev.map(session =>
        session.id === sessionId
          ? { ...session, lastMessage: text }
          : session
      )
    );
  };

  const joinSession = (sessionId) => {
    const session = activeSessions.find(s => s.id === sessionId);
    if (session) {
      setCurrentSession(session);
    }
  };

  const leaveSession = () => {
    setCurrentSession(null);
  };

  const setTyping = (typing) => {
    setIsTyping(typing);
  };

  const value = {
    activeSessions,
    currentSession,
    messages,
    sendMessage,
    joinSession,
    leaveSession,
    isTyping,
    setTyping
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
};
